// Dependency imports
import React, {
  SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid,
  IconButton,
  Slider,
  Stack,
  Switch,
  Tab,
  Tabs
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import VideoCameraBackIcon from '@mui/icons-material/VideoCameraBack';
import { useWebSocket } from 'hooks';
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Description } from '@mui/icons-material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';

// Local imports
import { RootState } from 'store';
import AnnotationTable from './annotationTable';
import { Button, Typography, Card, Modal } from 'components';
import { useAppDispatch } from 'hooks';
import { SelectInput } from 'components/Form';
import {
  getConfiguration,
  useAddToActivityInference,
  useGetAllActivities,
  useGetConfiguration,
  useGetWorkflowDetail,
  useListConfigurations,
  useListInferenceModels,
  useListInferenceVideos
} from 'api/sdk';
import { AppDispatch, selectSite } from 'store';
import { useSnack } from 'plugins/snack';
import Env from 'config/env';
import SearchSelect from 'components/SearchSelect';
import {
  consumeWebsocketMessage,
  fetchVideos,
  selectInferenceState,
  updateConfigId,
  uploadFiles
} from 'store/inference';
import LiveTable from './liveTable';
import { InferenceMode } from 'schemas';
import { setPreventNavigation } from 'store/preventNavigation/preventNavigationSlice';
import useVideo from 'hooks/video';
import { NotificationList } from './notificationList';

// Styles imports
import './styles.css';
import { setPreviousModel } from 'store/config/cameraLive/cameraSlice';
import { setConfiguration } from 'store/configuration/configurationSlice';
import DependancyGraph from 'pages/configuration/AnalysisConfiguration/WorkflowMonitor/dependancyGraph';
import { resetFilterModel } from 'store/pagination/paginationSlice';
import { hrsToMnsToSecToMlSec } from 'utils';
import WorkflowModal from 'pages/train/workflowModal';

const WorkFlowAnnotation = () => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const appDispatch = useAppDispatch();
  const { data: configs } = useListConfigurations();
  const siteId = useSelector(
    (data: any) => data?.auth?.profile?.session_site?.id
  );
  const currentConfigId = useSelector(
    (state: RootState) => state.configuration.configuration
  );
  const [videoReportQuery, setVideoReportQuery] = useState<any>();
  const { data: getConfigurationData } = useGetConfiguration({
    config_id: currentConfigId || ''
  });
  const { data: allActivities } = useGetAllActivities(currentConfigId || '', {
    pipeline_satus: true || null
  });
  const [videoName, setVideoName] = useState<string | null>('');
  const [isPlay, setPlay] = useState<boolean>(false);
  const [isPause, setPause] = useState<boolean>(true);
  const [selectedModel, setSelectedModel] = useState<string>('');
  const [modelOpen, setModelOpen] = useState<boolean>(false);
  const [videoModalOpen, setVideoModalOpen] = useState<boolean>(false);
  const [selected, setSelected] = useState<string[]>([]);
  const [value, setValue] = useState(0);
  const { data: workflowData, refetch: refetchWorkflow } = useGetWorkflowDetail(
    selectedModel || ''
  );
  const [getCurrentTime, setCurrentTime] = useState<number>(0);
  const [selectedWorkflowData, setWorkflowData] = useState<any>([]);
  const [currentVideo, setCurrentVideo] = useState<string | null>();
  const [heatmapVideo, setHeatmapVideo] = useState<string | null>(null);
  const [lastTime, setLastTime] = useState(0);
  const [videoDuration, setVideoDuration] = useState<number>(0);
  const { videoRef, seek, reset } = useVideo();
  const [getVideoCurrentTime, setVicdeoCurrentTime] = useState<number>(0);
  const { loading, videos } = useSelector(selectInferenceState);
  const site = useSelector(selectSite);
  const snack = useSnack();
  const dispatch = useDispatch<AppDispatch>();
  const [isVideoUploading, setIsVideoUploading] = useState<boolean>(false);
  const [toggleVideo, setToggleVideo] = useState<boolean>(false);
  const ws_url = `${Env.WEBSOCKET_URL}/ws/inference/${site?.id}`;
  const { socket } = useWebSocket(ws_url);
  const [isInferedVideo, setIsInferedVideo] = useState(false);
  const [loadingProgress, setLoading] = useState<boolean>(true);
  const [selectedWorkflowId, setSelectedWorkflowId] = useState<string>('');
  const [disableSelect, setDisableSelect] = useState(true);
  const [error, setError] = useState<string>('');

  const handleMessagesReceived = useCallback(
    (message: any) => {
      dispatch(consumeWebsocketMessage(message));
    },
    [dispatch]
  );

  useEffect(() => {
    if (socket) {
      const messageHandler = (event: any) => {
        const message = JSON.parse(event.data);
        handleMessagesReceived(message);
      };
      socket.addEventListener('message', messageHandler);
      return () => socket.removeEventListener('message', messageHandler);
    }
  }, [socket, handleMessagesReceived]);

  const handleButtonClick = useCallback(() => {
    fileInputRef?.current?.click();
  }, []);

  const handlePlay = useCallback(() => {
    setPlay(false);
    setPause(true);
    if (videoRef.current) {
      videoRef.current.play();
    }
  }, []);

  const handlePause = useCallback(() => {
    setPlay(true);
    setPause(false);
    if (videoRef.current) {
      videoRef.current.pause();
    }
  }, []);

  const {
    mutateAsync: addActivityInference,
    isLoading: addActivityInferenceLoading,
    error: addActivityInferenceError
  } = useAddToActivityInference();

  const {
    data: models,
    refetch: refetchModels,
    isLoading: modelsLoading
  } = useListInferenceModels(
    siteId,
    {
      config_id: currentConfigId
    },
    {
      query: {
        enabled: !!currentConfigId
      }
    }
  );

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (currentConfigId === 'Select Configuration') {
      setError('Please select configuration');
      return;
    }
    setError('');
    const files = event.target.files;
    if (!files) return;
    dispatch(
      uploadFiles({
        configuration_id: currentConfigId || '',
        files,
        model_id: selectedModel,
        site_id: site?.id || '',
        showDuplicateError
      })
    );
    event.target.value = '';
  };

  useEffect(() => {
    const model = models?.data
      .filter(item => {
        if (item.status === 'COMPLETED') {
          return item;
        }
      })
      .map(model => ({
        label: model.model_name,
        value: model.id
      }));
    model && setSelectedModel(model[0]?.value);
  }, [modelOpen]);

  useEffect(() => {
    setPause(true);
    setPlay(false);
    setToggleVideo(false);
  }, [videoModalOpen]);

  const showDuplicateError = (message: string) => {
    snack({
      message: message,
      severity: 'error'
    });
  };

  useEffect(() => {
    if (currentConfigId !== 'Select Configuration') {
      refetchModels();
      setSelectedModel('');
      setSelected([]);
      setModelOpen(false);
    }
  }, [currentConfigId, refetchModels]);

  useEffect(() => {
    if (currentConfigId) {
      dispatch(
        fetchVideos({
          config_id: currentConfigId,
          mode: value === 0 ? InferenceMode.OFFLINE : InferenceMode.LIVE
        })
      );
    }
  }, [value, currentConfigId]);

  useEffect(() => {
    const checkBuffering = () => {
      const video = videoRef.current;
      if (video) {
        if (
          videoDuration !== 0 &&
          (video.currentTime === videoDuration ||
            video.currentTime > videoDuration)
        ) {
          setPlay(true);
          setPause(false);
        }

        if (video.currentTime > videoDuration) {
          setLoading(false);
        } else if (
          video.currentTime === lastTime &&
          !isPlay &&
          video.currentTime !== videoDuration
        ) {
          setLoading(true);
        } else {
          setLoading(false);
        }
        setLastTime(video.currentTime);
      }
    };

    const interval = setInterval(checkBuffering, 500); // Check every 500ms

    return () => {
      clearInterval(interval);
    };
  }, [lastTime]);

  function videoCurrentTime(event: SyntheticEvent<HTMLVideoElement, Event>) {
    setVicdeoCurrentTime(event?.currentTarget?.currentTime);
  }

  // Toggle between normal and heatmap video
  const handleToggleChange = useCallback(() => {
    setToggleVideo(prev => !prev);
    if (videoRef.current) {
      reset(
        !toggleVideo
          ? `${heatmapVideo}#t=${getVideoCurrentTime}`
          : `${currentVideo}#t=${getVideoCurrentTime}`
      );
      seek(getVideoCurrentTime);
    }

    handlePause();

    setTimeout(() => {
      if (videoRef.current) {
        handlePlay();
        videoRef.current.currentTime = getVideoCurrentTime;
      }
    }, 1000);
  }, [
    toggleVideo,
    heatmapVideo,
    currentVideo,
    getVideoCurrentTime,
    reset,
    seek,
    handlePause,
    handlePlay
  ]);

  // Generate nodes
  const nodes = useMemo(() => {
    if (!selectedModel || !selectedWorkflowData) return [];

    if (getConfigurationData?.data?.config_type === 'pick_and_place') {
      const workflowConfig =
        selectedWorkflowData?.data?.workflow[0]?.workflow_config;

      if (!workflowConfig || !Array.isArray(workflowConfig)) return [];

      return workflowConfig.map((item: any) => ({
        id: item.id,
        data: {
          label: item.label,
          mandatory: item.mandatory
        },
        position: {
          x: Math.random() * 500,
          y: Math.random() * 400
        }
      }));
    } else {
      if (
        !workflowData?.data ||
        !Array.isArray(workflowData.data) ||
        !selectedWorkflowId
      ) {
        return [];
      }

      const selectedWorkflow = workflowData.data.find(
        (workflow: any) => workflow.id === selectedWorkflowId
      );

      if (!selectedWorkflow || !selectedWorkflow.data?.workflow) {
        return [];
      }

      return selectedWorkflow.data.workflow.map((item: any, index: number) => ({
        id: item.id,
        data: {
          label:
            allActivities?.data?.activity_details?.find(
              (activity: any) => activity.id === item.activity_id
            )?.activity || '',
          mandatory: item.mandatory || false
        },
        position: {
          x: 200 * (index % 3),
          y: Math.floor(index / 3) * 100
        }
      }));
    }
  }, [selectedModel, selectedWorkflowData, getConfigurationData, workflowData]);

  // Generate edges
  const edges = useMemo(() => {
    if (!selectedModel || !selectedWorkflowData) return [];

    if (getConfigurationData?.data?.config_type === 'pick_and_place') {
      const workflowConfig =
        selectedWorkflowData?.data?.workflow[0]?.workflow_config;

      if (!workflowConfig) return [];

      return workflowConfig
        .filter(
          (item: any) => item.dependency && Array.isArray(item.dependency)
        )
        .flatMap((item: any) =>
          item.dependency.map((dep: any) => ({
            id: `e-${dep}-${item.id}`,
            source: dep,
            target: item.id,
            type: 'smoothstep',
            animated: true
          }))
        );
    } else {
      if (
        !workflowData?.data ||
        !Array.isArray(workflowData.data) ||
        !selectedWorkflowId
      ) {
        return [];
      }

      const selectedWorkflow = workflowData.data.find(
        (workflow: any) => workflow.id === selectedWorkflowId
      );

      // If no workflow found or no workflow data, return empty array
      if (!selectedWorkflow || !selectedWorkflow.data?.workflow) {
        return [];
      }

      // Now we can safely access the workflow array
      return selectedWorkflow.data.workflow
        .filter(
          (item: any) => item.dependency && Array.isArray(item.dependency)
        )
        .flatMap((item: any) =>
          item.dependency.map((dep: any) => ({
            id: `e-${dep}-${item.id}`,
            source: dep,
            target: item.id,
            type: 'smoothstep',
            animated: true
          }))
        );
    }
  }, [selectedModel, selectedWorkflowData, getConfigurationData, workflowData]);

  const rows = useMemo(() => {
    return videos.map(video => {
      let obj: any = {};
      obj['id'] = video['id'];
      obj['configuration_id'] = video['configuration_id'];
      obj['created_at'] = video['created_at'] || '-';
      obj['duration'] = video['duration'];
      obj['fail_reason'] = video['fail_reason'];
      obj['graph_data'] = video['graph_data'];
      obj['inference_end_time'] = video['inference_end_time']
        ? video['inference_end_time']
        : '-';
      obj['inference_start_time'] = video['inference_start_time'];
      obj['model'] = video?.model?.model_name;
      obj['name'] = video['name'];
      obj['output_url'] = video['output_url'];
      obj['output_video'] = video['output_video'];
      obj['output_video_url'] = video['output_video_url'];
      obj['status'] = video['status'];
      obj['upload_progress'] = video['upload_progress'];
      obj['process_progress'] = video['process_progress'];
      obj['upload_path'] = video['upload_path'];
      obj['video_url'] = video['video_url'];
      const isSequenceTrue = (video as any)?.graph_data?.sequence?.every(
        (seq: any) => seq.every((val: boolean) => val)
      );
      obj['seq_check'] =
        video['status'] === 'COMPLETED'
          ? isSequenceTrue
            ? 'Valid'
            : 'Invalid'
          : '-';
      return obj;
    });
  }, [videos]);

  useEffect(() => {
    const hasUploadingVideos = videos.some(
      video => video.upload_progress && video.upload_progress < 100
    );
    setIsVideoUploading(hasUploadingVideos);
  }, [videos]);

  useEffect(() => {
    if (isVideoUploading) {
      dispatch(setPreventNavigation(true));

      // Initial state push
      window.history.pushState(
        { shouldPreventNavigation: true },
        '',
        window.location.pathname
      );

      // Handle page refresh and closing
      const handleBeforeUnload = (e: BeforeUnloadEvent) => {
        if (isVideoUploading) {
          // Modern browsers will show their standard "Changes you made may not be saved" message
          e.preventDefault();
        }
      };

      // Handle browser back button and in-app navigation
      const handlePopState = (event: PopStateEvent) => {
        if (isVideoUploading) {
          // Prevent the default navigation
          event.preventDefault();

          const confirmNavigation = window.confirm(
            'Data is currently uploading. If you leave this page, the upload will be lost. Are you sure you want to proceed?'
          );

          if (!confirmNavigation) {
            // Stay on the current page by pushing a new state
            window.history.pushState(
              { shouldPreventNavigation: true },
              '',
              window.location.pathname
            );
          } else {
            // Allow navigation by removing the upload state and going back
            setIsVideoUploading(false);
            dispatch(setPreventNavigation(false));
            window.history.back();
          }
        }
      };

      // Block navigation attempts
      const blockNavigation = (e: Event) => {
        if (isVideoUploading) {
          e.preventDefault();
          return false;
        }
      };

      // Set up event listeners
      window.onbeforeunload = handleBeforeUnload;
      window.addEventListener('popstate', handlePopState);
      window.addEventListener('beforeunload', blockNavigation);

      // Cleanup function
      return () => {
        window.onbeforeunload = null;
        window.removeEventListener('popstate', handlePopState);
        window.removeEventListener('beforeunload', blockNavigation);
        dispatch(setPreventNavigation(false));
      };
    } else {
      // Clear handlers if not uploading
      window.onbeforeunload = null;
    }
  }, [isVideoUploading, dispatch]);

  // Improved checkStatus function
  const checkStatus = useCallback(
    (temp: Array<any>) => {
      if (!Array.isArray(temp)) return;

      const hasUploading = temp.some(
        (_item, index) => rows[index]?.status === 'UPLOADING'
      );

      setIsVideoUploading(hasUploading);
      setDisableSelect(hasUploading);

      // Push state when upload starts
      if (hasUploading) {
        window.history.pushState(
          { shouldPreventNavigation: true },
          '',
          window.location.pathname
        );
      }
    },
    [rows]
  );

  useEffect(() => {
    if (models?.data && models?.data.length > 0) {
      setSelectedModel(models.data[0].id);
    }
  }, [models]);

  useEffect(() => {
    dispatch(resetFilterModel());
  }, []);

  const handleSliderChange = useCallback(
    (event: Event, value: number | number[]) => {
      const newTime = Array.isArray(value) ? value[0] : value;

      if (newTime === videoDuration) {
        setLoading(false);
      }
      if (videoRef.current) {
        videoRef.current.currentTime = newTime;
        setVicdeoCurrentTime(newTime);
      }
    },
    [videoDuration]
  );

  const handleLoadedMetadata = useCallback(() => {
    if (videoRef.current) {
      videoRef.current.currentTime = 0;
      setVideoDuration(videoRef.current.duration);
    }
  }, []);
  // selectedWorkflowData?.data?.workflow

  const handleClickStart = () => {
    addActivityInference({
      configId: currentConfigId || '',
      params: {
        site_id: site?.id || ''
      },
      data: [
        ...selected.map((s: any) => {
          if (!selectedWorkflowId) {
            snack({
              message: 'Workflow configuration is required',
              severity: 'warning'
            });
            throw new Error('Workflow configuration is required');
          }
          return {
            model_id: selectedModel,
            workflow_config_id: selectedWorkflowId,
            video_id: s
          };
        })
      ]
    }).then(response => {
      if (response.status === 200) {
        setSelected([]);
        setModelOpen(false);
        if (currentConfigId) {
          dispatch(
            fetchVideos({
              config_id: currentConfigId,
              mode: value === 0 ? InferenceMode.OFFLINE : InferenceMode.LIVE
            })
          );
        }

        snack({
          message: 'Inference started successfully',
          severity: 'success'
        });
      }
    });
  };

  // Memoized time display
  const formattedTime = useMemo(() => {
    return {
      current: hrsToMnsToSecToMlSec(videoRef?.current?.currentTime || 0),
      total: hrsToMnsToSecToMlSec(videoRef?.current?.duration || 0)
    };
  }, [videoRef?.current?.currentTime, videoRef?.current?.duration]);

  useEffect(() => {
    // setSelectedWorkflowId()
    const selected = models?.data
      .filter(model => model?.id === selectedModel)[0]
      ?.workflow_configurations?.map((config: any) => ({
        label: config.name || 'Unnamed',
        value: config.id
      }));
    selected && setSelectedWorkflowId(selected[0]?.value);

    if (selected) {
      const selectedWorkflow: any = models?.data
        ?.flatMap(model => model.workflow_configurations || [])
        .find(workflow => workflow.id === (selected[0]?.value || ''));

      setWorkflowData(selectedWorkflow);
    }
  }, [selectedModel]);

  interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
  }

  function CustomTabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;

    return (
      <div
        role="tabpanel"
        hidden={value !== index}
        id={`simple-tabpanel-${index}`}
        aria-labelledby={`simple-tab-${index}`}
        {...other}
      >
        {value === index && <Box sx={{ p: 0 }}>{children}</Box>}
      </div>
    );
  }

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };
  // Add this memoized modal near your return statement
  const memoizedVideoModal = useMemo(
    () => (
      <Modal
        open={videoModalOpen}
        onClose={() => {
          setPlay(false);
          setPause(true);
          setVideoModalOpen(false);
          setIsInferedVideo(false);
          if (videoRef.current) {
            videoRef.current.pause();
            setVicdeoCurrentTime(0);
          }
        }}
        size="xl"
      >
        {currentVideo && (
          <div>
            <Stack>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography>{videoName}</Typography>
                {getConfigurationData?.data?.config_type ===
                  'spatio_temporal' &&
                  isInferedVideo && (
                    <FormControl component="fieldset" variant="standard">
                      <FormGroup>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={toggleVideo}
                              onChange={handleToggleChange}
                              name="toggleVideo"
                              color="primary"
                              disabled={value === 1}
                            />
                          }
                          label="Hotspot"
                          sx={{
                            color: 'black',
                            zIndex: 20
                          }}
                        />
                      </FormGroup>
                    </FormControl>
                  )}
              </Stack>

              <video
                ref={videoRef}
                disablePictureInPicture
                autoPlay
                src={currentVideo}
                style={{ maxWidth: '100%', maxHeight: '70vh' }}
                controls={false}
                onTimeUpdate={videoCurrentTime}
                onLoadedMetadata={handleLoadedMetadata}
              />
              {loadingProgress && (
                <CircularProgress
                  size={60}
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)'
                  }}
                />
              )}
              <Stack
                direction={'column'}
                alignItems={'center'}
                gap={2}
                marginTop={3}
                style={{ fontWeight: 'bold' }}
              >
                {formattedTime?.current} / {formattedTime?.total}
              </Stack>

              <Stack
                direction={'row'}
                alignItems={'center'}
                gap={2}
                marginTop={3}
              >
                {isPlay && (
                  <IconButton onClick={handlePlay}>
                    <PlayArrowIcon />
                  </IconButton>
                )}
                {isPause && (
                  <IconButton onClick={handlePause}>
                    <PauseIcon />
                  </IconButton>
                )}
                <Slider
                  value={getVideoCurrentTime}
                  max={videoDuration}
                  onChange={handleSliderChange}
                  aria-labelledby="continuous-slider"
                  style={{ width: '100%' }}
                />
              </Stack>
            </Stack>
            {isInferedVideo && (
              <Stack>
                <NotificationList
                  videoQuery={videoReportQuery}
                  selectedConfig={currentConfigId}
                  getVideoCurrentTime={getVideoCurrentTime}
                  setVicdeoCurrentTime={setCurrentTime}
                  seek={seek}
                  mode={value}
                />
              </Stack>
            )}
          </div>
        )}
      </Modal>
    ),
    [
      videoModalOpen,
      currentVideo,
      videoName,
      isInferedVideo,
      toggleVideo,
      value,
      loadingProgress,
      isPlay,
      isPause,
      getVideoCurrentTime,
      videoDuration,
      videoReportQuery,
      currentConfigId
    ]
  );

  return (
    <div className="w-full p-4 flex flex-col gap-2">
      <div className="flex w-full items-center justify-between">
        <h2 className="text-[1.1vw] font-[Proxima-Nova-Bold]">Inference</h2>
        <div className="flex items-center justify-end gap-2">
          {configs && (
            <SearchSelect
              label="Select Configuration"
              onChange={(value: any) => {
                dispatch(setConfiguration(value));
              }}
              selectValue={currentConfigId}
              data={[
                ...(configs?.data.map((config: any) => ({
                  label: config.name,
                  value: config.id
                })) || [])
              ]}
              placeholder="Configuration"
              width="12vw"
              disabled={isVideoUploading}
            />
          )}
          <Button
            onClick={() => {
              dispatch(setPreviousModel(''));
              navigate(`/inference/live`);
            }}
            disabled={isVideoUploading}
            sx={{
              float: 'right',
              opacity: isVideoUploading ? 0.6 : 1,
              cursor: isVideoUploading ? 'not-allowed' : 'pointer',
              '&.Mui-disabled': {
                opacity: 0.6,
                cursor: 'not-allowed',
                pointerEvents: 'auto' // This ensures the cursor style is applied even when disabled
              }
            }}
            noTransition={isVideoUploading}
          >
            <>
              <VideoCameraBackIcon sx={{ marginRight: '6px' }} />
              Live
            </>
          </Button>
          {value === 0 && (
            <Button
              onClick={handleButtonClick}
              style={{
                float: 'right'
              }}
            >
              <>
                <AddIcon sx={{ marginRight: '6px' }} />
                Video
              </>
            </Button>
          )}
          <input
            type="file"
            ref={fileInputRef}
            accept="video/*"
            multiple
            capture="user"
            style={{ display: 'none' }}
            onChange={handleFileChange}
          />
        </div>
      </div>
      <div className="w-full">
        <Grid item xs={12} marginTop={'10px'}>
          <Box sx={{ width: '100%' }}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs
                value={value}
                onChange={handleChange}
                aria-label="basic tabs example"
              >
                <Tab
                  label="Offline"
                  sx={{ fontSize: '14px', fontWeight: 'bold' }}
                />
                <Tab
                  label="Live"
                  sx={{
                    fontSize: '14px',
                    fontWeight: 'bold',
                    cursor: isVideoUploading ? 'not-allowed' : 'pointer',
                    '&.Mui-disabled': {
                      opacity: 0.6,
                      cursor: 'not-allowed',
                      pointerEvents: 'auto'
                    }
                  }}
                  disabled={isVideoUploading}
                />
              </Tabs>
            </Box>
            <CustomTabPanel value={value} index={0}>
              <AnnotationTable
                rows={rows}
                checkStatus={checkStatus}
                loading={loading}
                selected={selected}
                setSelected={setSelected}
                handleButtonClick={handleButtonClick}
                isVideoUploading={isVideoUploading}
                setVideoModalOpen={setVideoModalOpen}
                setVideoName={setVideoName}
                setCurrentVideo={setCurrentVideo}
                setHeatmapVideo={setHeatmapVideo}
                setVideoReportQuery={setVideoReportQuery}
                setIsInferedVideo={setIsInferedVideo}
              />
            </CustomTabPanel>
            <CustomTabPanel value={value} index={1}>
              <LiveTable
                rows={rows}
                loading={loading}
                selected={selected}
                setSelected={setSelected}
                handleButtonClick={handleButtonClick}
                setVideoModalOpen={setVideoModalOpen}
                setVideoName={setVideoName}
                setCurrentVideo={setCurrentVideo}
                setVideoReportQuery={setVideoReportQuery}
                setIsInferedVideo={setIsInferedVideo}
              />
            </CustomTabPanel>
          </Box>
          <Alert severity="error" style={{ display: error ? 'block' : 'none' }}>
            {error} Please select configuration and model
          </Alert>
        </Grid>
        {currentConfigId !== 'Select Configuration' && (
          <Grid
            item
            xs={12}
            display={'flex'}
            justifyContent={'center'}
            marginTop={'20px'}
          >
            <Button
              children={'Start Inference'}
              disabled={disableSelect || selected.length === 0}
              onClick={() =>
                models?.data.filter(item => {
                  if (item.status === 'COMPLETED') {
                    return item;
                  }
                }).length
                  ? setModelOpen(true)
                  : snack({
                      message:
                        'No trained model found, Please train the model to start the inference.',
                      severity: 'warning'
                    })
              }
              size={'medium'}
              style={{
                background: '#2AB5B7',
                opacity: !disableSelect && selected.length > 0 ? 1 : 0.6,
                cursor:
                  !disableSelect && selected.length > 0
                    ? 'pointer'
                    : 'not-allowed'
              }}
            />
          </Grid>
        )}
      </div>
      <Modal
        open={modelOpen}
        onClose={() => setModelOpen(false)}
        title="Select Model & Workflow"
        size="xl"
      >
        <Stack>
          <div className="flex flex-row items-start justify-between gap-4 w-full">
            <div className="flex flex-col gap-3 flex-1">
              <div className="flex flex-row items-center flex-start gap-3">
                <SearchSelect
                  data={
                    models?.data
                      .filter(item => {
                        if (item.status === 'COMPLETED') {
                          return item;
                        }
                      })
                      .map(model => ({
                        label: model.model_name,
                        value: model.id
                      })) || []
                  }
                  selectValue={selectedModel}
                  label="Select Model"
                  placeholder="Model"
                  // variant="outlined"
                  onChange={value => setSelectedModel(value)}
                />
                <SearchSelect
                  data={
                    selectedModel &&
                    models?.data
                      .filter(model => model?.id === selectedModel)[0]
                      ?.workflow_configurations?.map((config: any) => ({
                        label: config.name || 'Unnamed',
                        value: config.id
                      }))
                  }
                  onChange={(value: string) => {
                    setSelectedWorkflowId(value);
                    const selectedWorkflow: any = models?.data
                      ?.flatMap(model => model.workflow_configurations || [])
                      .find(workflow => workflow.id === value);
                    setWorkflowData(selectedWorkflow);
                  }}
                  label="Select Workflow"
                  selectValue={selectedWorkflowId}
                  placeholder="Workflow"
                  width="10vw"
                  disabled={false}
                />
              </div>
              <DependancyGraph nodes={nodes} edges={edges} />
            </div>
          </div>

          {addActivityInferenceError && (
            <Alert severity="error">{addActivityInferenceError.message}</Alert>
          )}
        </Stack>
        <div
          style={{
            marginTop: '20px',
            display: 'flex',
            justifyContent: 'center'
          }}
        >
          <Button
            children={'Proceed'}
            onClick={() => {
              if (!selectedWorkflowId) {
                snack({
                  message: 'Workflow configuration is required',
                  severity: 'warning'
                });
              } else if (selectedModel && selectedWorkflowId) {
                handleClickStart();
              }
            }}
            disabled={!selectedModel || !selectedWorkflowId}
            loading={addActivityInferenceLoading}
            size={'medium'}
          />
        </div>
      </Modal>

      {memoizedVideoModal}
    </div>
  );
};

export default React.memo(WorkFlowAnnotation);
